// Copyright 2022 Linka Cloud  All rights reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Check, ContentCopyOutlined } from '@mui/icons-material';
import { Box, IconButton, SxProps } from '@mui/material';
import copy from 'copy-to-clipboard';
import Highlight, { defaultProps, Language } from 'prism-react-renderer';
import { useState } from 'react';
import './xonokai.css';

export interface CodeProps {
  code: string;
  language?: Language;
  color?: string;
  preSx?: SxProps;
}

export const Code = ({ code, language, color, preSx }: CodeProps) => {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = async () => {
    copy(code, {
      format: 'text/plain',
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };
  return (
    <Box>
      <Box component="code" sx={{ position: 'relative', display: 'block' }}>
        <IconButton
          sx={{
            zIndex: 2,
            position: 'absolute',
            top: 7,
            right: 7,
            opacity: copied ? 1 : 0.5,
            color: copied ? 'primary.main' : 'inherit',
          }}
          onClick={copyToClipboard}
        >
          {copied ? <Check /> : <ContentCopyOutlined />}
        </IconButton>
        <Box
          sx={{
            backgroundColor: 'black',
            color,
            paddingRight: 6,
          }}
        >
          {language ? (
            <Highlight {...defaultProps} theme={undefined} code={code} language={language}>
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                <Box
                  className={className}
                  style={style}
                  component="pre"
                  sx={{
                    ...preSx,
                    padding: 2,
                    overflow: 'scroll',
                  }}
                >
                  {tokens.map((line, key) => (
                    <Box {...getLineProps({ line })} key={key}>
                      {line.map((token, key) => (
                        <Box component="span" {...getTokenProps({ token })} key={key} />
                      ))}
                    </Box>
                  ))}
                </Box>
              )}
            </Highlight>
          ) : (
            <Box
              component="pre"
              sx={{
                ...preSx,
                padding: 2,
                overflow: 'scroll',
              }}
            >
              {code}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

Code.displayName = 'Code';
